// src/constants.js - Centralize all magic strings

// ============================================================================
// CATEGORY CONSTANTS
// ============================================================================

export const CATEGORIES = {
  CHASSIS: 'Chassis',
  RACING_CONTROLS: 'Racing Controls',
  DISPLAY: 'Display',
  DISPLAY_AND_AUDIO: 'Display & Audio',
  DISPLAYS: 'Displays',
  FLIGHT_CONTROLS: 'Flight Controls',
  CHASSIS_UPGRADES: 'Chassis Upgrades',
  SHIPPING: 'Shipping',
  OTHER: 'Other'
};

export const SUBCATEGORIES = {
  GENERAL: 'General',
  WHEEL_RIM: 'Wheel Rim',
  WHEEL_BASE: 'Wheel Base',
  SINGLE_DISPLAYS: 'Single Displays',
  TRIPLE_DISPLAYS: 'Triple Displays',
  SINGLE_DISPLAY_STANDS: 'Single Display Stands',
  TRIPLE_DISPLAY_STANDS: 'Triple Display Stands',
  CHASSIS: 'Chassis'
};

export const DISPLAY_TYPES = {
  SINGLE: 'Single',
  TRIPLE: 'Triple',
  VR: 'VR'
};

// ============================================================================
// COMPATIBILITY MAPPINGS
// ============================================================================

export const BRAND_DEPENDENT_CATEGORIES = {
  [SUBCATEGORIES.WHEEL_RIM]: [SUBCATEGORIES.WHEEL_BASE],
  [SUBCATEGORIES.WHEEL_BASE]: [SUBCATEGORIES.WHEEL_RIM]
};

export const DISPLAY_DEPENDENT_CATEGORIES = {
  [SUBCATEGORIES.SINGLE_DISPLAYS]: [SUBCATEGORIES.TRIPLE_DISPLAYS, SUBCATEGORIES.TRIPLE_DISPLAY_STANDS],
  [SUBCATEGORIES.SINGLE_DISPLAY_STANDS]: [SUBCATEGORIES.TRIPLE_DISPLAYS, SUBCATEGORIES.TRIPLE_DISPLAY_STANDS],
  [SUBCATEGORIES.TRIPLE_DISPLAYS]: [SUBCATEGORIES.SINGLE_DISPLAYS, SUBCATEGORIES.SINGLE_DISPLAY_STANDS],
  [SUBCATEGORIES.TRIPLE_DISPLAY_STANDS]: [SUBCATEGORIES.SINGLE_DISPLAYS, SUBCATEGORIES.SINGLE_DISPLAY_STANDS]
};

// ============================================================================
// UTILITY FUNCTIONS - Using Constants
// ============================================================================

export const isChassisCategory = (categoryName) => {
  return categoryName?.toLowerCase() === CATEGORIES.CHASSIS.toLowerCase();
};

export const isShippingCategory = (categoryName) => {
  return categoryName?.toLowerCase() === CATEGORIES.SHIPPING.toLowerCase();
};

export const isGeneralSubcategory = (subcategoryName) => {
  return subcategoryName === SUBCATEGORIES.GENERAL;
};

export const isDisplayCategory = (categoryName) => {
  if (!categoryName) return false;
  const cat = categoryName.toLowerCase();
  return cat === CATEGORIES.DISPLAY.toLowerCase() || 
         cat === CATEGORIES.DISPLAYS.toLowerCase() || 
         cat === CATEGORIES.DISPLAY_AND_AUDIO.toLowerCase();
};

export const isWheelRimCategory = (subcategoryName) => {
  return subcategoryName === SUBCATEGORIES.WHEEL_RIM;
};

export const isFlightCategory = (categoryName) => {
  return categoryName === CATEGORIES.FLIGHT_CONTROLS;
};

export const isRacingControlsCategory = (categoryName) => {
  return categoryName === CATEGORIES.RACING_CONTROLS;
};

export const isSeatsCategory = (categoryName) => {
  if (!categoryName) return false;
  const lower = categoryName.toLowerCase();
  return lower === 'seats' || lower === 'seat';
};

// Helper function to check if a category or subcategory allows multiple selections
export const allowsMultipleSelection = (category, categoryConfig) => {
  if (!category || !categoryConfig) return false;
  return categoryConfig[category]?.allowMultipleSelection === true;
};

// ============================================================================
// FALLBACK ICONS (moved from AppUtils)
// ============================================================================

export const FALLBACK_CATEGORY_ICONS = {
  [SUBCATEGORIES.OTHER]: '📦',
  [SUBCATEGORIES.GENERAL]: '⚙️'
};

// ============================================================================
// ADMIN MODE DETECTION
// ============================================================================

export const ADMIN_SLUG = 'admin';

export const isAdminMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const slug = params.get('slug');
  const adminParam = params.get('admin');

  // Admin mode if slug=admin OR admin=true parameter exists
  return (slug && slug.toLowerCase() === ADMIN_SLUG) || adminParam === 'true';
};

// ============================================================================
// BUSINESS LOGIC FUNCTIONS
// ============================================================================

export const isLockedCategory = (categoryName) => {
  // In admin mode, nothing is locked
  if (isAdminMode()) {
    return false;
  }

  // For regular users, chassis and shipping are locked
  return isChassisCategory(categoryName) || isShippingCategory(categoryName);
};

export const isLockedProduct = (product, mainCategory, subCategory) => {
  // In admin mode, no products are locked
  if (isAdminMode()) {
    return false;
  }

  // For regular users, chassis products are locked
  return isLockedCategory(mainCategory) || isLockedCategory(subCategory);
};

// ============================================================================
// MAGIC NUMBERS & CONFIGURATION
// ============================================================================

// Sorting & Ordering
export const DEFAULT_CATEGORY_ORDER = 999;
export const DEFAULT_SUBCATEGORY_ORDER = 999;

// API Configuration
export const API_LIMITS = {
  DEFAULT_LIMIT: 50,
  MAX_LIMIT: 1000,
  DEFAULT_TIMEOUT: 10000, // 10 seconds
  MAX_RESPONSE_SIZE: 10 * 1024 * 1024 // 10MB
};

// Price Validation
export const PRICE_LIMITS = {
  MIN: 0,
  MAX: 1000000
};

// URL Parameter Validation
export const URL_PARAM_MAX_LENGTH = {
  SLUG: 200,
  ID: 200,
  BUILD: 2000
};

// Local Storage Keys
export const STORAGE_KEYS = {
  BUILD_VIEWED: 'vesaro-build-viewed',
  ADMIN_ACCESS: 'vesaro-admin-access'
};

// ============================================================================
// COMPULSORY CATEGORIES & SUBCATEGORIES
// ============================================================================

/**
 * Check if a category is compulsory based on baseConfig data
 * @param {string} categoryName - The category name to check
 * @param {Object} compulsoryData - The compulsory categories/subcategories from baseConfig
 * @returns {boolean}
 */
export const isCompulsoryCategory = (categoryName, compulsoryData) => {
  if (!categoryName || !compulsoryData) return false;
  if (isAdminMode()) return false; // Admin mode bypasses all compulsory checks

  const { compulsoryCategories = [], compulsorySubcategories = [] } = compulsoryData;

  // Check in compulsoryCategories array
  const isCompulsoryMain = compulsoryCategories.some(cat =>
    cat.categoryName === categoryName || cat._id === categoryName
  );

  // Check in compulsorySubcategories array
  const isCompulsorySub = compulsorySubcategories.some(sub =>
    sub.subCategoryName === categoryName || sub._id === categoryName
  );

  return isCompulsoryMain || isCompulsorySub;
};

/**
 * Get all compulsory categories that are missing selections
 * @param {Object} selectedProducts - Currently selected products
 * @param {Object} compulsoryData - The compulsory categories/subcategories from baseConfig
 * @param {Object} productData - All available products grouped by category
 * @returns {Array} Array of missing compulsory category/subcategory names
 */
export const getMissingCompulsoryCategories = (selectedProducts, compulsoryData, productData) => {
  if (isAdminMode() || !compulsoryData) return [];

  const { compulsoryCategories = [], compulsorySubcategories = [] } = compulsoryData;
  const missing = [];

  // Check compulsory main categories
  compulsoryCategories.forEach(cat => {
    const categoryName = cat.categoryName;
    // Check if any product from this category is selected
    const hasSelection = Object.values(selectedProducts).some(
      product => product.mainCategory === categoryName
    );

    if (!hasSelection) {
      missing.push({ type: 'category', name: categoryName });
    }
  });

  // Check compulsory subcategories
  compulsorySubcategories.forEach(sub => {
    const subCategoryName = sub.subCategoryName;
    // Check if any product from this subcategory is selected
    const hasSelection = Object.values(selectedProducts).some(
      product => product.subCategory === subCategoryName
    );

    if (!hasSelection) {
      missing.push({ type: 'subcategory', name: subCategoryName });
    }
  });

  return missing;
};